import React, { useState } from 'react';
import PropTypes from 'prop-types';

import ChevronDown from '@decisiv/iconix/lib/components/ChevronDown';
import ChevronUp from '@decisiv/iconix/lib/components/ChevronUp';
import Popover from '@decisiv/ui-components/lib/components/Popover';
import Tag from '@decisiv/ui-components/lib/components/Tag';

import { OverflowPanel } from './styles';

/**
 * The "expand for the rest" affordance used by the accounts and permissions
 * cells and by the Edit User header: a chevron trigger that flips up while its
 * panel is open.
 *
 * Visibility is controlled rather than left to Popover so the chevron can track
 * it. The panel is a Popover plus plain markup rather than the KeyDS Menu, which
 * crashes on the React 16 portal pins (see ManagementLayout/UserMenu.jsx).
 *
 * The trigger is always the design's single pill — the chevron and its divider
 * sit inside one KeyDS Tag, drawn by the design system rather than by CSS of
 * ours (PD-5022). `count` is the accounts shape, the number of entries hidden in
 * an outline tag; `label` is the permissions shape, a name in the same pastel
 * tag those cells use inline. Exactly one of the two.
 */
function OverflowPopover({ ariaLabel, children, count, label }) {
  const [isOpen, setIsOpen] = useState(false);
  const toggle = () => setIsOpen((open) => !open);
  const Chevron = isOpen ? ChevronUp : ChevronDown;

  // KeyDS gives the Tag `tabIndex={0}` and an onClick but no key handling, so a
  // keyboard user could focus the pill and not open it. It has to say what it is
  // as well, since a Tag is a div and Popover only adds aria-haspopup and
  // aria-expanded to whatever element it is handed.
  const keyboardActivation = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    toggle();
  };

  const triggerTag =
    count === undefined
      ? { color: 'information', palette: 'pastel', text: label }
      : { text: String(count), variant: 'outline' };

  // KeyDS draws the action only when text, action and actionIcon are all given,
  // so losing any one of them would drop the chevron silently.
  const target = (
    <Tag
      {...triggerTag}
      action={toggle}
      actionAttributes={{
        'aria-label': ariaLabel,
        onKeyDown: keyboardActivation,
        role: 'button',
      }}
      actionIcon={Chevron}
    />
  );

  return (
    <Popover
      hideOnClickOutside
      onRequestHide={() => setIsOpen(false)}
      placement="bottom-start"
      showArrow={false}
      visible={isOpen}
      target={target}
    >
      <OverflowPanel>{children}</OverflowPanel>
    </Popover>
  );
}

OverflowPopover.propTypes = {
  ariaLabel: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  /** How many entries are hidden. Renders the outline pill; excludes `label`. */
  count: PropTypes.number,
  /** Names what the panel holds. Renders the pastel pill; excludes `count`. */
  label: PropTypes.string,
};

OverflowPopover.defaultProps = {
  count: undefined,
  label: undefined,
};

export default OverflowPopover;
